import type { State } from '../State';
import { encodePacket } from '../utils/encoder';
import {
  inviteChallengeDuration,
  inviteChallengeTarget,
  InviteChallengeWallpapers,
  inviteFriendListSelectionLimit,
  inviteRewardEnergy,
  inviteRewardGems,
  inviteRewardMoves,
  InviteRewardType,
  minigamesToUnlockInvite,
  recentInviteDuration,
  ReferralRewardType,
  SocialSubFeature,
} from './social.ruleset';
import * as Inventory from '../inventory';
import * as Tutorial from '../tutorial';
import features from '../ruleset/features';
import { PartialRecord } from '../utils/types';
import { teaHash } from '@play-co/replicant';
import type { InviteAsyncOptions } from '@play-co/gcinstant';
import { duration } from '../utils/duration';
import { isPromoReady } from '../promo/promo.getters';

export const hasMetUnlockCriteria = (state: State) => {
  // Number of puzzles played so far is current level minus 1
  const minigameCount = state.match2.level - 1;
  return minigameCount >= minigamesToUnlockInvite;
};

export function isFriendInviteAvailable(state: State): boolean {
  if (!features(state).friendReferral) return false;

  if (Tutorial.getters.isTutorialStepComplete(state, 'friendReferral')) {
    // Feature already unlocked
    return true;
  }

  return hasMetUnlockCriteria(state);
}

export function isSocialContinueAvailable(state: State, now: number): boolean {
  // Currently there isn't a way to validate invitation sent via native share picker,
  // so we should not provide a mean to abuse game continuation here.
  return false;
}

/**
 * Return the cooldown duration for for invites
 * @param state
 * @param now
 * @returns
 */
export function getRecentInviteDuration(canShop: boolean): number {
  // No invite cooldown if user can't shop with IAP
  if (!canShop) return 0;

  // default duration
  return recentInviteDuration;
}

export const getExcludedFriendsForInvite = (
  state: State,
  friendIds: string[],
  now: number,
  canShop: boolean,
) =>
  friendIds.filter((id) => {
    const maskedId = encodePacket(id);
    const invite = state.social.invites[maskedId];
    return invite?.timeSent > now - getRecentInviteDuration(canShop);
  });

export function getInviteReward(
  _state: State,
  subFeature: SocialSubFeature,
): { type: InviteRewardType; value: number } {
  const gemsReward = inviteRewardGems;

  switch (subFeature) {
    case 'invite_no_energy':
      return { type: 'energy', value: inviteRewardEnergy };

    case 'continue_social':
      return { type: 'moves', value: inviteRewardMoves };

    default:
      return { value: gemsReward, type: 'gems' };
  }
}

export function getInviteLimit(
  state: State,
  subFeature: SocialSubFeature,
  now: number,
): number {
  let limit = inviteFriendListSelectionLimit;
  if (subFeature === 'invite_no_energy') {
    const { maxRewardedEnergy } = Inventory.ruleset;
    const currentEnergy = Inventory.getters.getEnergy(state, now);
    limit = Math.min(limit, maxRewardedEnergy - currentEnergy);
  }

  // Ensure we don't declare a negative limit
  return Math.max(limit, 0);
}

export function isInviteChallengeRunning(state: State, now: number): boolean {
  if (!features(state).friendReferral) return false;

  const challengeStart = state.social.challenge.startTime;
  return (
    challengeStart > 0 &&
    now <= challengeStart + inviteChallengeDuration &&
    !state.social.challenge.claimed
  );
}

export const isInviteChallengeAvailable = (state: State) =>
  !state.social.challenge.claimed;

export const isInviteChallengeComplete = (state: State) =>
  state.social.challenge.counter >= getInviteChallengeTarget(state);

export const getInviteChallengeTarget = (state: State) => inviteChallengeTarget;

/**
 * Get current wallpaper theme for invite challenge
 */
export function getCurrentChallengeWallpaper(
  state: State,
): InviteChallengeWallpapers {
  return 'kingkong';
}

/**
 * Get referral pending rewards
 */
export const getPendingReferralRewards = (state: State) =>
  state.social.referral.pendingRewards.reduce(
    (acc, { reward }) => {
      const { type, value } = reward;
      acc[type] = (acc[type] ?? 0) + value;
      return acc;
    },
    {} as PartialRecord<ReferralRewardType, number>,
  );

/**
 * Has pending rewards
 */
export const hasPendingReferralRewards = (state: State, now: number) => {
  // For enabled in-game friends feature flag, skip if referral rewards collection is on cooldown.
  if (!isPromoReady(state, 'inGameFriendReferralRewardsCollection', now)) {
    return false;
  }

  return state.social.referral.pendingRewards.some(
    ({ reward }) => reward.value > 0,
  );
};

/**
 * Generates a hexadecimal hash string for a given payload using the Tiny Encryption Algorithm (TEA).
 *
 * @param payload - The payload object containing the feature and subFeature strings.
 * @param payload.feature - The main feature string.
 * @param payload.subFeature - The sub-feature string.
 * @returns A hexadecimal string representing the hash of the payload.
 *
 * @example
 * ```typescript
 * const hash = hashPayload({ feature: 'featureName', subFeature: 'subFeatureName' });
 * console.log(hash); // Outputs a hex string like "b7452a"
 * ```
 */
function hashPayload(payload: Record<string, string>): string {
  // 1. Generate initial hash using TEA (Tiny Encryption Algorithm)
  // teaHash returns a float between 0 and 1 (e.g., 0.7163972854614258)
  const hash = teaHash(JSON.stringify(payload));

  // 2. Convert float to hex string:
  // - 0x1000000 is 16777216 in decimal (2^24)
  // - Multiply our float (0-1) by 0x1000000 to get a number between 0 and 16777215
  // - Math.floor() ensures we get a clean integer
  // - toString(16) converts to hex string (base 16)
  // Example: 0.7163972854614258 * 16777216 = 12012345 = "b7452a" in hex
  return ((hash * 0x1000000) | 0).toString(16);
}

function stringifyCacheValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;

  return JSON.stringify(value);
}

function getShareLinkHashPayload(
  opts: InviteAsyncOptions,
): Record<string, string> {
  return {
    feature: stringifyCacheValue(opts.data.feature),
    subFeature: stringifyCacheValue(
      opts.data.$subFeature ?? opts.data.subFeature,
    ),
    creativeAssetID: stringifyCacheValue(opts.data.$creativeAssetID),
    creativeTextID: stringifyCacheValue(opts.data.$creativeTextID),
    cardId: stringifyCacheValue(opts.data.cardId),
    cardShareKind: stringifyCacheValue(opts.data.cardShareKind),
    challengeId: stringifyCacheValue(opts.data.challengeId),
    deepLink: stringifyCacheValue(opts.data.$deepLink),
  };
}

/**
 * Retrieves a cached share link if it is still valid and matches the given options.
 *
 * @param state - The current state containing the social share link information.
 * @param opts - The options used to generate the share link, including feature and subFeature data.
 * @param now - The current timestamp used to determine if the cached link is still valid.
 * @returns The cached share link ID if it is still valid and matches the given options, otherwise `undefined`.
 */
export function getShareLinkCache(
  state: State,
  opts: InviteAsyncOptions,
  now: number,
): { hash: string; linkId: string | undefined } {
  const expireLinkDuration = duration({ days: 1 });

  const hash = hashPayload(getShareLinkHashPayload(opts));

  // Use saved link if it's still valid and has the same hash
  if (state.social.shareLink && state.social.shareLink.hash === hash) {
    const delta = now - state.social.shareLink.createdAt;
    if (delta < expireLinkDuration) {
      return { hash, linkId: state.social.shareLink.id };
    }
  }

  return { hash, linkId: undefined };
}
